import { FC, useEffect } from "react";
import {
  Checkbox,
  FormControl,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { OnChangeEvent } from "types/general";

export interface MultipleSelectOption {
  value: number | string;
  label: string;
  subList?: MultipleSelectOption[];
}

interface MultipleSelectFieldProps {
  options: MultipleSelectOption[];
  selected?: number[];
  action: (values: number[]) => void;
  actionAll?: () => void;
  label?: string;
  allLabel?: string;
  fullWidth?: boolean;
  autoSelectAll?: boolean;
  hideLabel?: boolean;
  hasSublists?: boolean;
  hideAllOption?: boolean;
}

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 200,
      width: 240,
    },
  },
  variant: "menu" as const,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "center" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "center" as const },
};

const getLeafOptions = (options: MultipleSelectOption[], hasSublists?: boolean) =>
  hasSublists ? options.flatMap((option) => option.subList || []) : options;

const ALL_OPTION_VALUE = "all";

const getLeafValues = (options: MultipleSelectOption[], hasSublists?: boolean) =>
  getLeafOptions(options, hasSublists)
    .map((option) => option.value)
    .filter((value): value is number => typeof value === "number");

const uniqueNumbers = (values: number[]) =>
  values.filter((value, index) => values.indexOf(value) === index);

export const MultipleSelectField: FC<MultipleSelectFieldProps> = ({
  options,
  selected,
  action,
  actionAll,
  label,
  allLabel,
  fullWidth,
  autoSelectAll,
  hideLabel,
  hasSublists,
  hideAllOption,
}) => {
  const classes = useStyles();
  const selectedValues = selected || [];
  const leafOptions = getLeafOptions(options, hasSublists);
  const allValues = getLeafValues(options, hasSublists);
  const isAllSelected = allValues.length > 0 && allValues.length === selectedValues.length;
  const allText = allLabel || (label ? `All ${label}` : "All");

  useEffect(() => {
    if (!autoSelectAll || !allValues.length) return;
    action(allValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectAll, allValues.join("_")]);

  const toggleGroup = (group: MultipleSelectOption) => {
    const groupValues = (group.subList || [])
      .map((item) => item.value)
      .filter((value): value is number => typeof value === "number");
    const isGroupSelected = groupValues.every((value) => selectedValues.includes(value));

    action(
      isGroupSelected
        ? selectedValues.filter((value) => !groupValues.includes(value))
        : uniqueNumbers(selectedValues.concat(groupValues)),
    );
  };

  return (
    <FormControl className={classes.root}>
      {!hideLabel && label && <Typography className={classes.label}>{label}</Typography>}
      <Select
        multiple
        variant="outlined"
        value={selectedValues}
        className={`${classes.select} ${fullWidth ? classes.fullWidth : ""}`}
        onChange={(e: OnChangeEvent) => {
          const values = (e.target.value || []) as (number | string)[];
          if (!hasSublists && actionAll && values.includes(ALL_OPTION_VALUE)) {
            actionAll();
            return;
          }

          const clickedGroup = hasSublists
            ? options.find((group) => values.includes(group.value))
            : undefined;
          if (clickedGroup) {
            toggleGroup(clickedGroup);
            return;
          }

          action(values.filter((value): value is number => typeof value === "number"));
        }}
        renderValue={() => {
          if (hasSublists) {
            const selectedLabels = options.reduce<string[]>((labels, group) => {
              const groupValues = (group.subList || [])
                .map((item) => item.value)
                .filter((value): value is number => typeof value === "number");
              const isGroupSelected =
                groupValues.length > 0 &&
                groupValues.every((value) => selectedValues.includes(value));

              if (isGroupSelected) {
                labels.push(group.label);
                return labels;
              }

              (group.subList || []).forEach((option) => {
                if (selectedValues.includes(Number(option.value))) {
                  labels.push(option.label);
                }
              });
              return labels;
            }, []);

            return selectedLabels.join(", ");
          }

          return isAllSelected
            ? allText
            : leafOptions
                .filter((option) => selectedValues.includes(Number(option.value)))
                .map((option) => option.label)
                .join(", ");
        }}
        MenuProps={MENU_PROPS}
      >
        {!hasSublists && !hideAllOption ? (
          <MenuItem value={ALL_OPTION_VALUE}>
            <Checkbox checked={isAllSelected} />
            <ListItemText primary={allText} />
          </MenuItem>
        ) : null}
        {hasSublists
          ? options.reduce<JSX.Element[]>((items, group) => {
              const groupValues = (group.subList || [])
                .map((item) => item.value)
                .filter((value): value is number => typeof value === "number");
              const isGroupSelected =
                groupValues.length > 0 &&
                groupValues.every((value) => selectedValues.includes(value));
              const isGroupPartial =
                !isGroupSelected && groupValues.some((value) => selectedValues.includes(value));

              items.push(
                <MenuItem key={`group-${group.value}`} value={group.value}>
                  <Checkbox checked={isGroupSelected} indeterminate={isGroupPartial} />
                  <ListItemText primary={group.label} />
                </MenuItem>,
              );

              (group.subList || []).forEach((option) => {
                items.push(
                  <MenuItem key={option.value} value={option.value} className={classes.subListItem}>
                    <Checkbox checked={selectedValues.includes(Number(option.value))} />
                    <ListItemText primary={option.label} />
                  </MenuItem>,
                );
              });

              return items;
            }, [])
          : options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedValues.includes(Number(option.value))} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
      </Select>
    </FormControl>
  );
};

export const useStyles = makeStyles((theme) => ({
  root: {},
  label: {
    color: "#5F6164",
    fontSize: 14,
    marginBottom: 4,
    textTransform: "capitalize",
  },
  select: {
    minWidth: 150,
    width: 150,
  },
  fullWidth: {
    width: "100%",
  },
  subListItem: {
    paddingLeft: 32,
  },
  "@media (max-width: 840px)": {
    root: {
      flex: 1,
    },
    select: {
      width: "100%",
      minWidth: 0,
    },
  },
}));
